import { cn } from "@/lib/utils"
import { CalendarIcon, ClockIcon } from "lucide-react"

interface Deadline {
  id: string
  title: string
  dueDate: string
  status: "urgent" | "upcoming" | "completed"
  type: "assignment" | "interview" | "submission" | "session"
}

interface UpcomingDeadlinesCardProps {
  /**
   * List of deadlines
   */
  deadlines?: Deadline[]

  /**
   * Additional CSS classes
   */
  className?: string
}

const statusConfig = {
  urgent: {
    label: "Due Soon",
    color: "text-destructive",
    bgColor: "bg-destructive/10",
    dotColor: "bg-destructive",
  },
  upcoming: {
    label: "Upcoming",
    color: "text-primary",
    bgColor: "bg-primary/10",
    dotColor: "bg-primary",
  },
  completed: {
    label: "Completed",
    color: "text-muted-foreground",
    bgColor: "bg-muted",
    dotColor: "bg-muted-foreground",
  },
}

const typeConfig = {
  assignment: "Assignment",
  interview: "Interview",
  submission: "Submission",
  session: "Session",
}

export function UpcomingDeadlinesCard({
  deadlines = [
    {
      id: "1",
      title: "Customer Interview #3",
      dueDate: "Today, 3:00 PM",
      status: "urgent",
      type: "interview",
    },
    {
      id: "2",
      title: "Weekly Data Submission",
      dueDate: "Tomorrow",
      status: "urgent",
      type: "submission",
    },
    {
      id: "3",
      title: "Brainstorming Assignment",
      dueDate: "Mar 20",
      status: "upcoming",
      type: "assignment",
    },
    {
      id: "4",
      title: "Live Class Session",
      dueDate: "Mar 22, 2:00 PM",
      status: "upcoming",
      type: "session",
    },
  ],
  className,
}: UpcomingDeadlinesCardProps) {
  return (
    <div
      className={cn(
        "bg-card border border-border rounded-lg p-6 space-y-4",
        className
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">
          Upcoming Deadlines
        </h3>
        <CalendarIcon className="h-5 w-5 text-muted-foreground" />
      </div>

      {/* Deadlines List */}
      {deadlines.length > 0 ? (
        <div className="space-y-3">
          {deadlines.map((deadline) => {
            const statusStyle = statusConfig[deadline.status]
            return (
              <div
                key={deadline.id}
                className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
              >
                {/* Status Dot */}
                <div className="flex items-center justify-center h-6 w-6 flex-shrink-0 mt-0.5">
                  <div
                    className={cn(
                      "h-2 w-2 rounded-full",
                      statusStyle.dotColor
                    )}
                  />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="font-medium text-sm text-foreground">
                      {deadline.title}
                    </div>
                    <span
                      className={cn(
                        "text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0",
                        statusStyle.bgColor,
                        statusStyle.color
                      )}
                    >
                      {typeConfig[deadline.type]}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <ClockIcon className="h-3 w-3" />
                    <span>{deadline.dueDate}</span>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <div className="text-center py-8">
          <CalendarIcon className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">
            No upcoming deadlines
          </p>
        </div>
      )}

      {/* View All Link */}
      {deadlines.length > 0 && (
        <div className="pt-2 border-t border-border">
          <button className="text-sm font-medium text-primary hover:text-primary/80 transition-colors">
            View All Deadlines
          </button>
        </div>
      )}
    </div>
  )
}